/**
 * AccountingPeriodService — the ONLY place where the accounting-month rule lives.
 *
 *   Cash (same_month):          purchase month = accounting month
 *   Credit card (next_month):   accounting month = following month (December → January of next year)
 *   Manual override:            the user-chosen month wins and is recorded as `manual_override`
 *
 * Dates are handled as ISO strings (`YYYY-MM-DD`, `YYYY-MM`) and plain integers, never `Date`
 * objects, so the result cannot shift with the server's time zone.
 *
 * See docs/decisions-and-conflicts.md (D3, D4) and docs/workbook-analysis.md §7.
 */
import type { AccountingRule, AppliedAccountingRule, PaymentMethodKind } from "@/domain/payment";

/** An accounting month as `YYYY-MM`. */
export type AccountingMonth = string;

export class AccountingPeriodError extends Error {
  override readonly name = "AccountingPeriodError";
}

export interface YearMonth {
  year: number;
  month: number; // 1..12
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_MONTH = /^(\d{4})-(\d{2})$/;

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function assertYear(year: number, input: string): void {
  if (year < 1900 || year > 2999) throw new AccountingPeriodError(`Year out of range: ${input}`);
}

/** Strictly parses `YYYY-MM-DD`; rejects impossible dates such as 2026-02-30. */
export function parseIsoDate(value: string): YearMonth & { day: number } {
  const m = ISO_DATE.exec(value);
  if (!m) throw new AccountingPeriodError(`Invalid date (expected YYYY-MM-DD): "${value}"`);
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  assertYear(year, value);
  if (month < 1 || month > 12) throw new AccountingPeriodError(`Invalid month in date: "${value}"`);
  if (day < 1 || day > daysInMonth(year, month)) throw new AccountingPeriodError(`Invalid day in date: "${value}"`);
  return { year, month, day };
}

/** Strictly parses `YYYY-MM`. */
export function parseAccountingMonth(value: string): YearMonth {
  const m = ISO_MONTH.exec(value);
  if (!m) throw new AccountingPeriodError(`Invalid accounting month (expected YYYY-MM): "${value}"`);
  const year = Number(m[1]);
  const month = Number(m[2]);
  assertYear(year, value);
  if (month < 1 || month > 12) throw new AccountingPeriodError(`Invalid accounting month: "${value}"`);
  return { year, month };
}

export function formatAccountingMonth({ year, month }: YearMonth): AccountingMonth {
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}`;
}

/** Adds `delta` months, carrying across year boundaries (2026-12 + 1 → 2027-01). */
export function addMonths(ym: YearMonth, delta: number): YearMonth {
  const zeroBased = ym.year * 12 + (ym.month - 1) + delta;
  return { year: Math.floor(zeroBased / 12), month: (zeroBased % 12) + 1 };
}

export interface ComputeAccountingMonthInput {
  /** Real purchase date, `YYYY-MM-DD`. Always stored separately from the accounting month. */
  purchaseDate: string;
  /** Rule of the payment method used. */
  rule: AccountingRule;
  /** Optional user override, `YYYY-MM`. Wins over the rule. */
  override?: string | null;
}

export interface AccountingMonthResult {
  accountingMonth: AccountingMonth;
  appliedRule: AppliedAccountingRule;
}

export function computeAccountingMonth(input: ComputeAccountingMonthInput): AccountingMonthResult {
  const purchase = parseIsoDate(input.purchaseDate);

  if (input.override != null && input.override !== "") {
    return {
      accountingMonth: formatAccountingMonth(parseAccountingMonth(input.override)),
      appliedRule: "manual_override",
    };
  }

  const purchaseMonth: YearMonth = { year: purchase.year, month: purchase.month };
  switch (input.rule) {
    case "same_month":
      return { accountingMonth: formatAccountingMonth(purchaseMonth), appliedRule: "same_month" };
    case "next_month":
      return { accountingMonth: formatAccountingMonth(addMonths(purchaseMonth, 1)), appliedRule: "next_month" };
    default: {
      const exhaustive: never = input.rule;
      throw new AccountingPeriodError(`Unknown accounting rule: ${String(exhaustive)}`);
    }
  }
}

/** First day of the accounting month (`YYYY-MM-01`) — the representation stored in Postgres. */
export function accountingMonthToDate(accountingMonth: AccountingMonth): string {
  return `${formatAccountingMonth(parseAccountingMonth(accountingMonth))}-01`;
}

/** Inverse of `accountingMonthToDate`; accepts any `YYYY-MM-DD` and returns its month. */
export function accountingMonthFromDate(isoDate: string): AccountingMonth {
  const { year, month } = parseIsoDate(isoDate);
  return formatAccountingMonth({ year, month });
}

/**
 * Suggested rule when a new payment method is created. Only credit cards are deferred by default;
 * every method stores its own rule, which the user can change (decision D4: Carta Unicredit →
 * next month; Contanti, PostePay, Satispay → same month).
 */
export function defaultRuleForPaymentKind(kind: PaymentMethodKind): AccountingRule {
  return kind === "credit_card" ? "next_month" : "same_month";
}

/**
 * Value for the workbook's `Data` column (decision D3). The monthly sheets derive the month with
 * `MONTH(Data)`, so `Data` must fall in the accounting month: the real purchase date when it
 * already does, otherwise the 1st of the accounting month. The real date goes to `Data Acquisto`.
 */
export function excelLedgerDate(purchaseDate: string, accountingMonth: AccountingMonth): string {
  const month = formatAccountingMonth(parseAccountingMonth(accountingMonth));
  return accountingMonthFromDate(purchaseDate) === month ? purchaseDate : `${month}-01`;
}

/** Negative when a < b, zero when equal, positive when a > b (`YYYY-MM` strings). */
export function compareMonths(a: AccountingMonth, b: AccountingMonth): number {
  const x = parseAccountingMonth(a);
  const y = parseAccountingMonth(b);
  return x.year * 12 + x.month - (y.year * 12 + y.month);
}

/** All months from `from` to `to`, inclusive. Empty if `from` is after `to`. */
export function monthRange(from: AccountingMonth, to: AccountingMonth): AccountingMonth[] {
  const out: AccountingMonth[] = [];
  let cursor = parseAccountingMonth(from);
  while (compareMonths(formatAccountingMonth(cursor), to) <= 0) {
    out.push(formatAccountingMonth(cursor));
    cursor = addMonths(cursor, 1);
  }
  return out;
}

export function monthsOfYear(year: number): AccountingMonth[] {
  return monthRange(`${year}-01`, `${year}-12`);
}

export function shiftMonth(month: AccountingMonth, delta: number): AccountingMonth {
  return formatAccountingMonth(addMonths(parseAccountingMonth(month), delta));
}

export function yearOfMonth(month: AccountingMonth): number {
  return parseAccountingMonth(month).year;
}
